import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { useSelector, useDispatch } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import { TextInput, TextArea } from "../../common_components/FormInputs";
import CommonLoader from "../../common_components/CommonLoader";
import { FloatingSaveBar } from "../../common_components/FloatingSaveBar";
import { fetchAllCredentials, upsertCredentials, deleteCredentials, selectIntegrationRecords, selectIntegrationLoading, selectIntegrationSaving, selectIntegrationDeleting, PLATFORMS, PLATFORM_LABELS, PLATFORM_ICONS, PLATFORM_FIELDS, } from "../../redux/integrationCredentialsSlice";
import IntegrationCredentialsHeader from "./Component/IntegrationCredentialsHeader";
import HubspotUsersPanel from "./Component/HubspotUsersPanel";
const OPTIONAL_FIELDS = new Set(["account"]);
const FIELD_LABELS = {
    account: "Account",
    client_id: "Client ID",
    client_secret: "Client Secret",
    developer_token: "Developer Token",
    mcc_id: "MCC ID",
    refresh_token: "Refresh Token",
    service_account_credentials: "Service Account Credentials (JSON)",
    access_token: "Access Token",
    customer_id: "Customer ID",
    version: "Version",
    active_client_list_board_id: "Active Client List Board ID",
    email: "Email",
    paid_media_projection_sheet_id: "Paid Media Projection Sheet ID",
    affiliate_projection_sheet_id: "Affiliate Projection Sheet ID",
    paid_media_commission_sheet_id: "Paid Media Commission Sheet ID",
    affiliate_commission_sheet_id: "Affiliate Commission Sheet ID",
    opted_out_contact_sheet_id: "Opted out Contact Sheet ID",
    company_id: "Company ID",
    username: "Username",
    password: "Password",
    api_key: "API Key",
};
const buildTabs = () => {
    const items = PLATFORMS.map((p) => ({
        key: p,
        label: PLATFORM_LABELS[p],
        icon: PLATFORM_ICONS[p],
        platform: p,
    }));
    const hubspotIdx = items.findIndex((t) => t.key === "hubspot");
    const usersTab = {
        key: "hubspot_users",
        label: "Hubspot Users",
        icon: PLATFORM_ICONS.hubspot,
        isUsers: true,
    };
    if (hubspotIdx !== -1)
        items.splice(hubspotIdx + 1, 0, usersTab);
    else
        items.push(usersTab);
    return items;
};
const TABS = buildTabs();
const TAB_COLUMNS = 6;
const DeleteIconSvg = () => (_jsx("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", children: _jsx("path", { d: "M2.5 5h1.667m0 0H17.5M4.167 5v11.667a1.667 1.667 0 0 0 1.666 1.667h8.334a1.667 1.667 0 0 0 1.666-1.667V5zm2.5 0V3.334a1.667 1.667 0 0 1 1.666-1.667h3.334a1.667 1.667 0 0 1 1.666 1.667V5m-5 4.167v5m3.334-5v5", stroke: "#535862", strokeWidth: "1.2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
const PlatformInitial = ({ label, size = 28 }) => (_jsx(Box, { sx: {
        width: size, height: size, borderRadius: "50%",
        background: "#ff008e15",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: size * 0.42, fontWeight: 700, color: "#ff008e", flexShrink: 0,
    }, children: label.charAt(0) }));
const IntegrationCredentialsMain = () => {
    const dispatch = useDispatch();
    const records = useSelector(selectIntegrationRecords);
    const loading = useSelector(selectIntegrationLoading);
    const saving = useSelector(selectIntegrationSaving);
    const deleting = useSelector(selectIntegrationDeleting);
    const [activeTab, setActiveTab] = useState(PLATFORMS[0]);
    const [formData, setFormData] = useState({});
    const [originalFormData, setOriginalFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [deleteState, setDeleteState] = useState({ open: false, platform: null });
    useEffect(() => { dispatch(fetchAllCredentials()); }, [dispatch]);
    const recordsByPlatform = useMemo(() => {
        const map = {};
        records.forEach((r) => { map[r.platform] = r; });
        return map;
    }, [records]);
    const isUsersTab = activeTab === "hubspot_users";
    const activePlatform = isUsersTab ? null : activeTab;
    useEffect(() => {
        if (!activePlatform) {
            setFormData({});
            setOriginalFormData({});
            setErrors({});
            return;
        }
        const existing = recordsByPlatform[activePlatform] || {};
        const fields = PLATFORM_FIELDS[activePlatform] || [];
        const initial = {};
        fields.forEach((f) => { initial[f] = existing[f] || ""; });
        setFormData(initial);
        setOriginalFormData(initial);
        setErrors({});
    }, [activePlatform, records]);
    const handleFieldChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field])
            setErrors((prev) => ({ ...prev, [field]: "" }));
    }, [errors]);
    const handleSave = useCallback(async () => {
        if (!activePlatform)
            return;
        const fields = PLATFORM_FIELDS[activePlatform] || [];
        const newErrors = {};
        fields.forEach((f) => {
            if (OPTIONAL_FIELDS.has(f))
                return;
            if (!formData[f]?.trim())
                newErrors[f] = `${FIELD_LABELS[f] || f} is required`;
        });
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        try {
            await dispatch(upsertCredentials({ platform: activePlatform, credentials: formData })).unwrap();
            setOriginalFormData(formData);
        }
        catch { }
    }, [activePlatform, formData, dispatch]);
    const handleResetChanges = useCallback(() => {
        setFormData(originalFormData);
        setErrors({});
    }, [originalFormData]);
    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteState.platform)
            return;
        try {
            await dispatch(deleteCredentials(deleteState.platform)).unwrap();
            setDeleteState({ open: false, platform: null });
        }
        catch { }
    }, [deleteState.platform, dispatch]);
    const activeTabItem = TABS.find((t) => t.key === activeTab) || TABS[0];
    const activeRecord = activePlatform ? recordsByPlatform[activePlatform] : undefined;
    const fields = activePlatform ? (PLATFORM_FIELDS[activePlatform] || []) : [];
    const isSavingThis = activePlatform ? saving === activePlatform : false;
    const changeCount = useMemo(() => {
        if (!activePlatform)
            return 0;
        return Object.keys(formData).filter((k) => (formData[k] || "") !== (originalFormData[k] || "")).length;
    }, [formData, originalFormData, activePlatform]);
    return (_jsxs(PageContainer, { children: [_jsx(Box, { className: "integration-credentials-card", children: _jsx(IntegrationCredentialsHeader, {}) }), _jsxs(Box, { sx: {
                    mt: 0.5, background: "#fff", borderRadius: "8px",
                    border: "1px solid #E5E7EB", display: "flex", flexDirection: "column",
                    height: "calc(100vh - 152px)", overflow: "hidden",
                }, children: [_jsx(Box, { sx: {
                            display: "grid",
                            gridTemplateColumns: `repeat(${TAB_COLUMNS}, 1fr)`,
                            width: "100%", flexShrink: 0, gap: "4px",
                            px: "8px", py: "8px",
                            borderBottom: "1px solid #f0f1f3",
                        }, children: TABS.map((tab) => {
                            const isActive = activeTab === tab.key;
                            return (_jsxs(Box, { component: "button", onClick: () => setActiveTab(tab.key), sx: {
                                    display: "flex", alignItems: "center", gap: "9px",
                                    px: "12px", py: "9px", border: "none", borderRadius: "8px",
                                    background: isActive ? "#fff0f7" : "transparent",
                                    cursor: "pointer", textAlign: "left", width: "100%",
                                    fontSize: 13, fontWeight: isActive ? 600 : 450,
                                    color: isActive ? "#ff008e" : "#4b5563",
                                    transition: "background 0.1s, color 0.1s",
                                    "&:hover": { background: isActive ? "#ffe0f0" : "#f9fafb", color: isActive ? "#ff008e" : "#111827" },
                                }, children: [_jsx(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, width: 22, height: 22 }, children: tab.icon
                                            ? _jsx("img", { src: tab.icon, alt: tab.label, style: { width: 18, height: 18, objectFit: "contain" } })
                                            : _jsx(PlatformInitial, { label: tab.label, size: 24 }) }), _jsx(Box, { sx: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: tab.label })] }, tab.key));
                        }) }), _jsx(Box, { sx: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }, children: loading ? (_jsx(Box, { sx: { display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : isUsersTab ? (_jsx(Box, { sx: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }, children: _jsx(HubspotUsersPanel, {}) })) : (_jsxs(Box, { sx: { flex: 1, overflowY: "auto", p: "24px 28px", position: "relative" }, children: [activePlatform && activeRecord && (_jsx(Box, { sx: { position: "absolute", top: 12, right: 16, zIndex: 1 }, children: _jsx(HoverTooltip, { title: "Clear All", children: _jsx(IconButton, { size: "small", onClick: () => setDeleteState({ open: true, platform: activePlatform }), children: _jsx(DeleteIconSvg, {}) }) }) })), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px 24px" }, children: fields.map((field) => (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: "6px", gridColumn: field === "service_account_credentials" ? "1 / -1" : undefined }, children: [_jsxs(Typography, { component: "label", sx: { fontSize: 13, fontWeight: 500, color: "#374151", display: "flex", alignItems: "center", gap: "3px" }, children: [FIELD_LABELS[field] || field.replace(/_/g, " "), !OPTIONAL_FIELDS.has(field) && (_jsx(Typography, { component: "span", sx: { color: "#ef4444", fontSize: 13 }, children: "*" }))] }), field === "service_account_credentials" ? (_jsx(TextArea, { value: formData[field] || "", onChange: (e) => handleFieldChange(field, e.target.value), placeholder: "Paste the contents of your service_account_credentials.json file here", error: !!errors[field], errorMessage: errors[field], minRows: 8, fullWidth: true })) : (_jsx(TextInput, { value: formData[field] || "", onChange: (e) => handleFieldChange(field, e.target.value), placeholder: `Enter ${FIELD_LABELS[field] || field}`, error: !!errors[field], errorMessage: errors[field], fullWidth: true }))] }, field))) })] })) })] }), _jsx(FloatingSaveBar, { count: changeCount, onReset: handleResetChanges, onSave: handleSave, updating: isSavingThis, isVisible: changeCount > 0, saveLabel: "Save", message: `Unsaved edits in ${activeTabItem.label}`, countLabel: "Changes Pending" }), deleteState.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleting, title: "Are You Sure?", description: `You're about to delete ${PLATFORM_LABELS[deleteState.platform]} credentials. This action cannot be undone.`, confirmText: "Yes, Delete", cancelText: "No, Cancel", onClose: () => !deleting && setDeleteState({ open: false, platform: null }), onConfirm: handleDeleteConfirm }))] }));
};
export default IntegrationCredentialsMain;
